import dashboardBarChartTemplate from './dashboardBarChart.html';
import './dashboardBarChart.scss';

export default angular
  .module('main.dashboardPanel.dashboardBarChart', [])

  .directive('dashboardBarChart', () => {
    return {
      restrict: 'E',
      template: dashboardBarChartTemplate,
      scope: {
        series: '=', // data series array {data: {x: number, y: number}[], label: string}[]
        min: '=', // minimum axis value
        max: '=', // maximum axis value
        stacked: '=' // stacked chart
      },
      controller: ($scope, $filter, $element) => {
        const $t = $filter('translate');
        const datasetColors = {
          grey: 'rgba(0, 0, 0, 0.1)',
          blue: 'rgba(132, 166, 238, 0.9)',
          green: 'rgba(92, 210, 62, 0.5)',
          yellow: 'rgba(230, 181, 69, 0.4)',
          red: 'rgba(200, 30, 30, 0.4)',
          purple: 'rgba(183, 39, 230, 0.4)',
          cyan: 'rgba(39,168,230,0.4)'
        };

        let chart;

        init();

        function init() {
          $scope.$watch('series', () => {
            if ($scope.series) {
              drawChart();
            }
          }, true);

          $scope.$on('$destroy', function() {
            if (chart) {
              chart.destroy();
            }
          });
        }

        function drawChart() {
          if (!chart) {
            createChart();
          }

          $scope.showNoDataMessage = false;
          chart.data.datasets = chart.data.datasets || [];

          // Clear old data
          chart.data.datasets.forEach(ds => {
            ds.data = [];
          });

          // Apply input data to datasets
          $scope.series?.forEach((series, index) => {
            let dataset = chart.data.datasets[index];
            if (!dataset) {
              // create dataset if it was not created yet
              dataset = {};
              chart.data.datasets.push(dataset);
            }

            dataset.label = series.label;

            if (series.color && datasetColors[series.color]) {
              dataset.backgroundColor = datasetColors[series.color];
            } else {
              let color = Object.keys(datasetColors)
                .find(c => !chart.data.datasets.some(ds => ds.backgroundColor === datasetColors[c]));
              dataset.backgroundColor = datasetColors[color];
            }

            dataset.data = angular.copy(series.data || [])
              .filter((datum, index, arr) => { // Filter out multiple values for the same day
                return !arr.some((d, i) => i > index && moment(datum.x).isSame(moment(d.x), 'day'));
              });
          });

          $scope.showNoDataMessage = !$scope.series || !$scope.series.length || $scope.series.every(s => !s.data || !s.data.length);

          let timeValues = $scope.series?.map(series => series.data.map(d => d.x)).flat();

          // Set min and max OX axis values
          chart.options.scales.xAxes[0].ticks.min = $scope.min || (timeValues?.length
            ? moment(Math.min(...timeValues)).startOf('d').valueOf()
            : undefined);
          chart.options.scales.xAxes[0].ticks.max = ($scope.max ? $scope.max - 1 : undefined) || (timeValues?.length
            ? moment(Math.max(...timeValues)).endOf('d').valueOf()
            : undefined);

          chart.options.scales.xAxes[0].stacked = !!$scope.stacked;
          chart.options.scales.yAxes[0].stacked = !!$scope.stacked;

          chart.update();
        }

        function createChart() {
          let ctx = $($element).find('#dashboardBarChart')[0];
          let colorHover = 'rgba(142, 167, 218, 1)';
          let colorBlack = '#555555';
          Chart.defaults.global.defaultFontFamily = '\'Roboto\', Arial, sans-serif';

          Chart.Tooltip.positioners.custom = function(elements, eventPosition) {
            if (!elements.length) {
              return false;
            }
            let x = elements.reduce((acc, e) => e._model.x + acc, 0) / elements.length;
            let height = Math.max(...elements.map(e => e._model.base - e._model.y));
            let y = elements.find(e => height === e._model.base - e._model.y)._model.y;
            return { x, y: y + (height / 2) };
          };

          chart = new Chart(ctx, {
            type: 'bar',
            data: {
              datasets: [
                {
                  label: 'Data',
                  data: []
                }
              ]
            },
            options: {
              layout: {
                padding: {
                  top: 10
                }
              },
              scales: {
                xAxes: [
                  {
                    type: 'time',
                    stacked: false,
                    time: {
                      timezone: moment.tz.guess(),
                      unit: 'day',
                      parser: function(date) {
                        return moment(date).tz(moment.tz.guess()).startOf('d'); // fixing offsetGridLines bug
                      }
                    },
                    offset: true,
                    ticks: {
                      maxRotation: 0,
                      autoSkipPadding: 12
                    },
                    gridLines: {
                      borderDash: [4, 4],
                      zeroLineBorderDash: [4, 4],
                      drawTicks: true,
                      offsetGridLines: true,
                      color: 'rgba(0, 0, 0, 0.15)',
                      zeroLineColor: 'rgba(0, 0, 0, 0.15)'
                    }
                  }
                ],
                yAxes: [
                  {
                    ticks: {
                      maxTicksLimit: 6,
                      suggestedMin: 0,
                      callback: value => value % 1 === 0 ? value : undefined // skip non-whole values
                    },
                    gridLines: {
                      drawTicks: true,
                      drawBorder: true,
                      drawOnChartArea: false,
                      zeroLineColor: 'rgba(0, 0, 0, 0)',
                      borderDash: [0, 20],
                    }
                  }
                ]
              },
              legend: {
                display: false
              },
              hover: {
                mode: 'nearest',
                intersect: false
              },
              tooltips: {
                animationDuration: 0,
                caretPadding: 12,
                cornerRadius: 3,
                displayColors: $scope.series?.length > 1,
                backgroundColor: '#ffffff',
                titleFontColor: colorBlack,
                bodyFontColor: colorBlack,
                borderColor: 'rgba(0,0,0,0.05)',
                borderWidth: 1,
                xPadding: 12,
                yPadding: 8,
                xAlign: 'center',
                yAlign: 'bottom',

                // Hovering preference
                mode: 'index',
                axis: 'x',
                intersect: false,
                position: $scope.stacked ? 'average' : 'custom',

                callbacks: {
                  title: function(tooltipItems, data) {
                    let dataObj = data.datasets[tooltipItems[0].datasetIndex].data[tooltipItems[0].index];
                    let date = dataObj.x;
                    return moment(date).format('ll');
                  },
                  labelColor: function(tooltipItem, data) {
                    if (data.config.data.datasets?.length > 1) {
                      let dataObj = data.config.data.datasets[tooltipItem.datasetIndex];
                      return {
                        backgroundColor: dataObj.borderColor || dataObj.backgroundColor || '#dddddd',
                        borderColor: '#ffffff'
                      };
                    }
                  }
                }
              },
              animation: {
                duration: 300
              }
            }
          });
        }
      }
    };
  })
  .name;
